import { useQueryParams } from '@elentari/core'
import { observer } from 'mobx-react-lite'
import { useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { Column, HeaderGroup } from 'react-table'
import { BaseTable, IColumn } from 'src/components/BaseTable'
import {
  MaskFilter,
  SelectFilterGeneric
} from 'src/components/ColumnsFiltersTable'
import { useConfigTable } from 'src/hooks/useConfigTable'
import { tableStore } from 'src/stores/TableStore'
import { resourceTabela } from 'src/utils/resourcesTabela'
import { AddRecord, Paper, TableContainer } from '../../components'
import NoResultsTable from '../../components/NoResultsTable'
import Spacer from '../../components/Spacer'
import { PessoasColumns } from './PessoasColumns'
import pessoasStore from './store/store'
import { PessoasDetail, PessoasQueryFilter, tiposPessoa } from './types'
import { SpeedDialIcon } from '@material-ui/lab'
import { useIsAdmin } from '../lotes/hooks/useIsAdmin'
import { Autorenew as AutorenewIcon } from '@material-ui/icons'
import { SpeedDials } from 'src/components/SpeedDials'
import { SincronizarDialog } from './components/SincronizarDialog'

interface Props {
  onDelete: (id: string) => void
  checks?: {
    ids: string[]
    onCheck: (id: string) => void
  }
  authorization?: {
    canCreate: boolean
    canDelete: boolean
    canUpdate: boolean
  }
}

const PessoasTable = observer(({ onDelete, authorization }: Props) => {
  const { pathname } = useLocation()
  const history = useHistory()
  const isAdmin = useIsAdmin()
  const [open, setOpen] = useState(false)

  const KEY_TABLE = resourceTabela.PESSOAS
  useConfigTable(KEY_TABLE)
  const tableState = tableStore.getState(KEY_TABLE)
  const isTableReady =
    !pessoasStore.pessoas.isLoading &&
    tableState &&
    pessoasStore.pessoas.list.length

  const { initialValues } = useQueryParams<PessoasQueryFilter>()
  const [columnsByTipo, setColumnsByTipo] = useState<IColumn[]>([])

  const actions = [
    {
      icon: <SpeedDialIcon />,
      name: 'Criar Produto',
      action: () => {
        history.push(`${pathname}/new`)
      }
    },
    ...(isAdmin
      ? [
          {
            icon: <AutorenewIcon />,
            name: 'Sincronizar Pessoa - Rodopar',
            action: () => {
              setOpen(true)
            }
          }
        ]
      : [])
  ]

  useEffect(() => {
    if (initialValues.tipo === 'PF') {
      setColumnsByTipo([
        {
          Header: 'Nome',
          accessor: 'nome'
        },
        {
          Header: 'Tipo',
          accessor: 'tipo',
          Filter: props =>
            SelectFilterGeneric({
              column: props.column as HeaderGroup<{}>,
              options: tiposPessoa
            })
        },
        {
          Header: 'CPF',
          accessor: 'cpf',
          Filter: props =>
            MaskFilter({
              column: props.column as HeaderGroup<{}>,
              mask: '999.999.999-99'
            })
        },
        {
          Header: 'Endereço',
          accessor: 'endereco',
          sort: false
        },
        {
          Header: 'Cidade/Estado',
          accessor: 'cidadeEstado',
          sort: false
        },
        {
          Header: 'Ativo',
          accessor: 'ativo',
          Filter: props =>
            SelectFilterGeneric({
              column: props.column as HeaderGroup<{}>,
              options: [
                {
                  label: 'Sim',
                  value: 'true'
                },
                {
                  label: 'Não',
                  value: 'false'
                }
              ],
              uppercase: false
            }),
          Cell: ({ value }: any) => <span>{value ? 'Sim' : 'Não'}</span>
        }
      ])
    } else if (initialValues.tipo === 'PJ') {
      setColumnsByTipo([
        {
          Header: 'Nome Fantasia',
          accessor: 'nomeFantasia'
        },
        {
          Header: 'Razão Social',
          accessor: 'razaoSocial'
        },
        {
          Header: 'Tipo',
          accessor: 'tipo',
          Filter: props =>
            SelectFilterGeneric({
              column: props.column as HeaderGroup<{}>,
              options: tiposPessoa
            })
        },
        {
          Header: 'CNPJ',
          accessor: 'cnpj'
        },
        {
          Header: 'Inscrição Estadual',
          accessor: 'inscricaoEstadual'
        },
        {
          Header: 'Endereço',
          accessor: 'endereco',
          sort: false
        },
        {
          Header: 'Cidade/Estado',
          accessor: 'cidadeEstado',
          sort: false
        },
        {
          Header: 'Ativo',
          accessor: 'ativo',
          Filter: props =>
            SelectFilterGeneric({
              column: props.column as HeaderGroup<{}>,
              options: [
                {
                  label: 'Sim',
                  value: 'true'
                },
                {
                  label: 'Não',
                  value: 'false'
                }
              ],
              uppercase: false
            }),
          Cell: ({ value }: any) => <span>{value ? 'Sim' : 'Não'}</span>
        }
      ])
    } else {
      setColumnsByTipo([
        {
          Header: 'Nome',
          accessor: 'nome'
        },
        {
          Header: 'Tipo',
          accessor: 'tipo',
          Filter: props =>
            SelectFilterGeneric({
              column: props.column as HeaderGroup<{}>,
              options: tiposPessoa
            })
        },
        {
          Header: 'Documento',
          accessor: 'documento'
        },
        {
          Header: 'Tipo Documento',
          accessor: 'tipoDocumento'
        },
        {
          Header: 'Endereço',
          accessor: 'endereco',
          sort: false
        },
        {
          Header: 'País',
          accessor: 'pais',
          sort: false
        },
        {
          Header: 'Ativo',
          accessor: 'ativo',
          Filter: props =>
            SelectFilterGeneric({
              column: props.column as HeaderGroup<{}>,
              options: [
                {
                  label: 'Sim',
                  value: 'true'
                },
                {
                  label: 'Não',
                  value: 'false'
                }
              ],
              uppercase: false
            }),
          Cell: ({ value }: any) => <span>{value ? 'Sim' : 'Não'}</span>
        }
      ])
    }
  }, [initialValues.tipo])

  const columns: Array<Column & IColumn> = useMemo(
    () => PessoasColumns({ columnsByTipo, onDelete, authorization }),
    [onDelete, authorization, columnsByTipo]
  )

  const rows = pessoasStore.pessoas.list.map((pessoa: PessoasDetail) => {
    const endereco = pessoa.enderecos?.[0]

    return {
      ...pessoa,
      endereco: endereco
        ? `${endereco.logradouro ?? ''}, ${endereco.numero ?? ''}`
        : '',

      cidadeEstado:
        endereco?.Cidade && endereco?.Estado
          ? `${endereco.Cidade.nome} / ${endereco.Estado.sigla}`
          : 'SEM CIDADE / SEM ESTADO',

      pais: endereco?.Pais?.nome ?? ''
    }
  })

  return (
    <>
      <Spacer y={4} />
      <Paper>
        <TableContainer
          loading={pessoasStore.pessoas.isLoading}
          totalCount={pessoasStore.pessoas.totalCount}
        >
          {isTableReady ? (
            <BaseTable
              keyTable={KEY_TABLE}
              columns={columns}
              hasConditionClickRow={false}
              linkWithRedirectClickRow={pathname}
              data={rows}
            />
          ) : (
            <NoResultsTable loading={pessoasStore.pessoas.isLoading} />
          )}
        </TableContainer>
      </Paper>
      <SpeedDials authorization={authorization} actions={actions} />
      <SincronizarDialog isOpen={open} handleClose={() => setOpen(false)} />
    </>
  )
})

export default PessoasTable
